import type { Calls, Config } from '../types';
import type WebRTCSession from './WebRTCSession';
import { CallSessionConnectionState, CallType, MediaType, PeerConnectionState } from '../types/constants';
import { RTCPeerConnection, RTCSessionDescription, MediaStream } from '@platform';
import config from '../config';
import WebRTCHelpers from './WebRTCHelpers';

export default class WebRTCPeerConnection {
  declare original: RTCPeerConnection;
  declare session: WebRTCSession;
  declare userID: number;
  declare type: RTCSdpType;
  public remoteSDP: string | null = null;
  public state: PeerConnectionState = PeerConnectionState.NEW;
  public iceCandidates: RTCIceCandidateInit[] = [];
  public answerTimeInterval: number = 0;
  public onStatusClosedChecker: NodeJS.Timeout | number | null = null;
  public dialingTimer: NodeJS.Timeout | number | null = null;
  public statsReportTimer: NodeJS.Timeout | number | null = null;
  public waitingReconnectTimeoutCallback: NodeJS.Timeout | number | null = null;
  public released: boolean = false;

  public remoteStream: MediaStream = (() => {
    if (!MediaStream) {
      throw new Error('MediaStream is not available');
    }

    return new MediaStream();
  })();

  constructor(session: WebRTCSession, userID: number, type: RTCSdpType) {
    this.create();
    this.setup(session, userID, type);
  }

  private create() {
    const rtcConfiguration: RTCConfiguration = {
      iceTransportPolicy: (config.videochat.alwaysRelayCalls ? 'relay' : 'all') as RTCIceTransportPolicy,
      iceServers: config.videochat.iceServers?.map(
        (item: Config.VideochatIceServer): RTCIceServer => ({
          urls: item.urls ?? item.url,
          username: item.username,
          credential: item.credential,
        })
      ) as RTCIceServer[],
    };

    this.original = RTCPeerConnection ? new RTCPeerConnection(rtcConfiguration) : ({} as RTCPeerConnection);

    WebRTCHelpers.trace(`new RTCPeerConnection(${JSON.stringify(rtcConfiguration, null, 2)})`);
  }

  private setup(session: WebRTCSession, userID: number, type: RTCSdpType): void {
    this.type = type;
    this.userID = userID;
    this.session = session;
    this.session.startCallTime = Date.now();

    this.original.addEventListener('track', this.onMediaTrackHandler);
    this.original.addEventListener('icecandidate', this.onIceCandidateHandler);
    this.original.addEventListener('signalingstatechange', this.onSignalingStateHandler);
    this.original.addEventListener('iceconnectionstatechange', this.onIceConnectionStateHandler);

    WebRTCHelpers.trace(`RTCPeerConnection init. ${this.toString()}`);
  }

  private onMediaTrackHandler = (event: RTCTrackEvent): void => {
    this.remoteStream.addTrack(event.track);

    if (
      (this.session.callType == CallType.VIDEO && this.remoteStream.getVideoTracks().length) ||
      (this.session.callType == CallType.AUDIO && this.remoteStream.getAudioTracks().length)
    ) {
      this.session.onRemoteStream(this.userID, this.remoteStream);
    }

    this.getWrappedStats();
  };

  private onIceCandidateHandler = (event: RTCPeerConnectionIceEvent): void => {
    if (event.candidate) {
      const { sdpMLineIndex, sdpMid, candidate } = event.candidate;
      const iceCandidate: RTCIceCandidateInit = { sdpMLineIndex, sdpMid, candidate };

      if (this.signalingState === 'stable' && this.original.canTrickleIceCandidates) {
        this.session.processIceCandidates(this.userID, [iceCandidate]);
      } else {
        this.iceCandidates.push(iceCandidate);
      }
    }
  };

  private onSignalingStateHandler = async (): Promise<void> => {
    WebRTCHelpers.trace(`onSignalingStateHandler: ${this.signalingState}`);

    if (this.signalingState === 'stable' && this.iceCandidates.length > 0) {
      this.session.processIceCandidates(this.userID, this.iceCandidates);
      this.iceCandidates.length = 0;
    }
  };

  /** handler of remote media stream */
  private onIceConnectionStateHandler = (): void => {
    WebRTCHelpers.trace(`onIceConnectionStateHandler: ${this.iceConnectionState}`);

    if (this.onStatusClosedChecker && WebRTCHelpers.getVersionSafari >= 11) {
      clearTimeout(this.onStatusClosedChecker);
    }

    switch (this.iceConnectionState) {
      case 'checking':
        this.state = PeerConnectionState.CHECKING;
        this.session.onSessionConnectionStateChanged(this.userID, CallSessionConnectionState.CONNECTING);
        break;
      case 'connected':
        this.state = PeerConnectionState.CONNECTED;
        this.clearWaitingReconnectTimer();
        this.session.onSessionConnectionStateChanged(this.userID, CallSessionConnectionState.CONNECTED);
        break;
      case 'completed':
        this.state = PeerConnectionState.COMPLETED;
        this.clearWaitingReconnectTimer();
        this.session.onSessionConnectionStateChanged(this.userID, CallSessionConnectionState.COMPLETED);
        break;
      case 'failed':
        this.state = PeerConnectionState.FAILED;
        this.session.onSessionConnectionStateChanged(this.userID, CallSessionConnectionState.FAILED);
        break;
      case 'disconnected':
        this.state = PeerConnectionState.DISCONNECTED;
        this.startWaitingReconnectTimer();
        this.session.onSessionConnectionStateChanged(this.userID, CallSessionConnectionState.DISCONNECTED);
        if (WebRTCHelpers.getVersionSafari >= 11) {
          this.onStatusClosedChecker = setTimeout(() => {
            this.onIceConnectionStateHandler(); // repeat to get status "closed"
          }, 500);
        }
        break;
      case 'closed': // TODO: this state doesn't fires on Safari 11
        this.state = PeerConnectionState.CLOSED;
        this.clearWaitingReconnectTimer();
        this.session.onSessionConnectionStateChanged(this.userID, CallSessionConnectionState.CLOSED);
        break;
      default:
        break;
    }
  };

  private clearWaitingReconnectTimer(): void {
    if (this.waitingReconnectTimeoutCallback) {
      WebRTCHelpers.trace('clearWaitingReconnectTimer');
      clearTimeout(this.waitingReconnectTimeoutCallback);
      this.waitingReconnectTimeoutCallback = null;
    }
  }

  private startWaitingReconnectTimer(): void {
    const timeout = (config.videochat.disconnectTimeInterval ?? 30) * 1000;

    WebRTCHelpers.trace(`startWaitingReconnectTimer, timeout: ${timeout}`);

    this.waitingReconnectTimeoutCallback = setTimeout(() => {
      WebRTCHelpers.trace('waitingReconnectTimeoutCallback');
      if (this.waitingReconnectTimeoutCallback) {
        clearTimeout(this.waitingReconnectTimeoutCallback);
      }
      this.release();
      this.session.closeSessionIfAllConnectionsClosed();
    }, timeout);
  }

  private clearStatsReportTimer(): void {
    if (this.statsReportTimer) {
      clearInterval(this.statsReportTimer);
      this.statsReportTimer = null;
    }
  }

  public async addCandidates(iceCandidates: RTCIceCandidateInit[]): Promise<void> {
    for (const iceCandidate of iceCandidates) {
      if (iceCandidate?.candidate) {
        try {
          await this.addIceCandidate(iceCandidate);
          WebRTCHelpers.trace(`'addIceCandidate' success: ${this.signalingState}`);
        } catch (error) {
          WebRTCHelpers.traceError(`'addIceCandidate' error: ${error}`);
        }
      }
    }
  }

  public release(): void {
    this.clearDialingTimer();
    this.clearStatsReportTimer();
    this.close();
    if (WebRTCHelpers.getVersionSafari >= 11) {
      this.onIceConnectionStateHandler(); // TODO: 'closed' state doesn't fires on Safari 11 (do it manually)
    }
    this.original.removeEventListener('track', this.onMediaTrackHandler);
    this.original.removeEventListener('icecandidate', this.onIceCandidateHandler);
    this.original.removeEventListener('signalingstatechange', this.onSignalingStateHandler);
    this.original.removeEventListener('iceconnectionstatechange', this.onIceConnectionStateHandler);
    this.released = true;
    WebRTCHelpers.trace(`RTCPeerConnection closed. ${this.toString()}`);
  }

  public clearDialingTimer(): void {
    if (this.dialingTimer) {
      WebRTCHelpers.trace('clearDialingTimer');
      clearInterval(this.dialingTimer);
      this.dialingTimer = null;
      this.answerTimeInterval = 0;
    }
  }

  public async getAndSetLocalSessionDescription(
    maxBandwidth: number,
    iceRestart: boolean = false
  ): Promise<RTCSessionDescriptionInit> {
    this.state = PeerConnectionState.CONNECTING;

    try {
      const offerOptions = iceRestart ? { iceRestart } : undefined;
      const sessionDescription =
        this.type === 'offer' ? await this.createOffer(offerOptions) : await this.createAnswer();

      await this.setLocalDescription(sessionDescription);
      this.setRTCRtpSenderMaxBandwidth(maxBandwidth);

      return sessionDescription;
    } catch (error) {
      throw error;
    }
  }

  public setRTCRtpSenderMaxBandwidth(maxBandwidth: number): void {
    const senders = this.getSenders() || [];
    const maxBitrate = maxBandwidth ? Math.round(maxBandwidth * 1000) : 0;

    senders.forEach((sender: RTCRtpSender) => {
      if (sender.track?.kind === MediaType.VIDEO) {
        const params = sender.getParameters();

        params.encodings ??= [{}];
        params.encodings.forEach((encoding) => {
          if (maxBitrate > 0) {
            encoding.maxBitrate = maxBitrate;
          } else {
            delete encoding.maxBitrate;
          }
        });

        sender
          .setParameters(params)
          .then(() => {
            WebRTCHelpers.trace(
              `Set 'maxBandwidth' for ${this.userID}: ${maxBandwidth > 0 ? `${maxBandwidth} kbps` : 'unlimited'}`
            );
          })
          .catch((error) => {
            WebRTCHelpers.traceWarning(`Set 'maxBandwidth' for ${this.userID}: ${error}`);
          });
      }
    });
  }

  public startDialingTimer(extension: Calls.ExtensionParams, withOnNotAnswerCallback: boolean): void {
    const dialingTimeInterval = (config.videochat.dialingTimeInterval ?? 5) * 1000;
    const answerTimeInterval = (config.videochat.answerTimeInterval ?? 60) * 1000;

    WebRTCHelpers.trace(`startDialingTimer, dialingTimeInterval: ${JSON.stringify(dialingTimeInterval)}`);

    const dialingCallback = (
      extension: Calls.ExtensionParams,
      withOnNotAnswerCallback: boolean,
      skipIncrement: boolean
    ) => {
      if (!skipIncrement) {
        this.answerTimeInterval += dialingTimeInterval;
      }

      WebRTCHelpers.trace(`dialingCallback, extension: ${JSON.stringify(extension)}`);

      if (this.answerTimeInterval >= answerTimeInterval) {
        this.clearDialingTimer();

        if (withOnNotAnswerCallback) {
          this.session.processOnNotAnswer(this);
        }
      } else {
        this.session.processCall(this, extension);
      }
    };

    this.dialingTimer = setInterval(dialingCallback, dialingTimeInterval, extension, withOnNotAnswerCallback, false);

    dialingCallback(extension, withOnNotAnswerCallback, true); // call for the 1st time
  }

  public async setRemoteSessionDescription(type: RTCSdpType, sdp: string): Promise<void> {
    const desc = new RTCSessionDescription!({ sdp, type });
    return this.setRemoteDescription(desc);
  }

  private getWrappedStats(): void {
    if (config.videochat.statsReportTimeInterval) {
      let lastResult: RTCStatsReport;

      WebRTCHelpers.trace('Stats tracker has been started');

      this.statsReportTimer = setInterval(() => {
        this.getStatsCustom(
          lastResult,
          (results, lastResults) => {
            lastResult = lastResults;
            this.session.onCallStatsReport(this.userID, results);
          },
          (err) => {
            WebRTCHelpers.traceError(`Get stats error. ${err.name}: ${err.message}`);
            this.session.onCallStatsReport(this.userID, null, err);
          }
        );
      }, config.videochat.statsReportTimeInterval * 1000);
    }
  }

  set sdpRemote(sdp: string) {
    if (sdp) {
      this.remoteSDP = sdp;
    }
  }

  get sdpRemote(): string | null {
    return this.remoteSDP;
  }

  toString(): string {
    return `sessionID: ${this.session.ID}, userID: ${this.userID}, type: ${this.type}, state: ${this.state}`;
  }

  private getStatsCustom(
    lastResults: RTCStatsReport,
    onSuccess: (
      stats: { local: Calls.StatsReportObj; remote: Calls.StatsReportObj },
      lastResults: RTCStatsReport
    ) => void,
    onFail: (err: Error) => void
  ) {
    const obj: Calls.StatsReportObj = { audio: {}, video: {}, candidate: {} };
    const statistic: { local: Calls.StatsReportObj; remote: Calls.StatsReportObj } = {
      local: Object.assign({}, obj),
      remote: Object.assign({}, obj),
    };

    if (WebRTCHelpers.getVersionFirefox) {
      const localStream = this.session.localStream;
      const localVideoSettings = localStream?.getVideoTracks()[0].getSettings();

      if (localVideoSettings) {
        statistic.local.video.frameHeight = localVideoSettings.height;
        statistic.local.video.frameWidth = localVideoSettings.width;
      }
    }

    this.getStats().then((results: RTCStatsReport) => {
      results.forEach((result) => {
        let item;

        if (result.bytesReceived && result.type === 'inbound-rtp') {
          item = statistic.remote[result.mediaType as keyof typeof obj];
          item.bitrate = this.getBitratePerSecond(result, lastResults, false);
          item.bytesReceived = result.bytesReceived;
          item.packetsReceived = result.packetsReceived;
          item.timestamp = result.timestamp;
          if (result.mediaType === MediaType.VIDEO && result.framerateMean) {
            item.framesPerSecond = Math.round(result.framerateMean * 10) / 10;
          }
        } else if (result.bytesSent && result.type === 'outbound-rtp') {
          item = statistic.local[result.mediaType as keyof typeof obj];
          item.bitrate = this.getBitratePerSecond(result, lastResults, true);
          item.bytesSent = result.bytesSent;
          item.packetsSent = result.packetsSent;
          item.timestamp = result.timestamp;
          if (result.mediaType === MediaType.VIDEO && result.framerateMean) {
            item.framesPerSecond = Math.round(result.framerateMean * 10) / 10;
          }
        } else if (result.type === 'local-candidate') {
          item = statistic.local.candidate;
          if (result.candidateType === 'host' && result.mozLocalTransport === 'udp' && result.transport === 'udp') {
            item.protocol = result.transport;
            item.ip = result.ipAddress;
            item.port = result.portNumber;
          } else if (!WebRTCHelpers.getVersionFirefox) {
            item.protocol = result.protocol;
            item.ip = result.ip;
            item.port = result.port;
          }
        } else if (result.type === 'remote-candidate') {
          item = statistic.remote.candidate;
          item.protocol = result.protocol || result.transport;
          item.ip = result.ip || result.ipAddress;
          item.port = result.port || result.portNumber;
        } else if (result.type === 'track' && result.kind === MediaType.VIDEO && !WebRTCHelpers.getVersionFirefox) {
          if (result.remoteSource) {
            item = statistic.remote.video;
            item.frameHeight = result.frameHeight;
            item.frameWidth = result.frameWidth;
            item.framesPerSecond = this.getFramesPerSecond(result, lastResults, false);
          } else {
            item = statistic.local.video;
            item.frameHeight = result.frameHeight;
            item.frameWidth = result.frameWidth;
            item.framesPerSecond = this.getFramesPerSecond(result, lastResults, true);
          }
        }
      });
      onSuccess(statistic, results);
    }, onFail);
  }

  private getBitratePerSecond(result: RTCStats, lastResults: RTCStatsReport, isLocal: boolean): number {
    const lastResult = lastResults.get(result.id);
    const seconds = lastResult ? (result.timestamp - lastResult.timestamp) / 1000 : 5;
    const kilo = 1024;
    const bit = 8;
    const r = result as RTCStats & { bytesSent?: number; bytesReceived?: number };
    const l = lastResult as (RTCStats & { bytesSent?: number; bytesReceived?: number }) | undefined;
    const bitrate = !lastResult
      ? 0
      : isLocal
        ? (bit * ((r.bytesSent ?? 0) - (l?.bytesSent ?? 0))) / (kilo * seconds)
        : (bit * ((r.bytesReceived ?? 0) - (l?.bytesReceived ?? 0))) / (kilo * seconds);

    return Math.round(bitrate);
  }

  private getFramesPerSecond(result: RTCStats, lastResults: RTCStatsReport, isLocal: boolean): number {
    const lastResult = lastResults && lastResults.get(result.id);
    const seconds = lastResult ? (result.timestamp - lastResult.timestamp) / 1000 : 5;
    const r = result as RTCStats & { framesSent?: number; framesReceived?: number };
    const l = lastResult as (RTCStats & { framesSent?: number; framesReceived?: number }) | undefined;
    const framesPerSecond = !lastResult
      ? 0
      : isLocal
        ? ((r.framesSent ?? 0) - (l?.framesSent ?? 0)) / seconds
        : ((r.framesReceived ?? 0) - (l?.framesReceived ?? 0)) / seconds;

    return Math.round(framesPerSecond * 10) / 10;
  }

  // original RTCPeerConnection methods
  close(): void {
    this.original.close?.();
  }
  addTrack(track: MediaStreamTrack, stream: MediaStream): RTCRtpSender {
    return this.original.addTrack?.(track, stream) ?? null;
  }
  getSenders(): RTCRtpSender[] {
    return this.original.getSenders?.() ?? null;
  }
  async createOffer(options?: RTCOfferOptions): Promise<RTCSessionDescriptionInit> {
    return (await this.original.createOffer?.(options)) ?? null;
  }
  async createAnswer(options?: RTCOfferOptions): Promise<RTCSessionDescriptionInit> {
    return (await this.original.createAnswer?.(options)) ?? null;
  }
  async getStats(selector?: MediaStreamTrack | null): Promise<RTCStatsReport> {
    return (await this.original.getStats?.(selector)) ?? null;
  }
  async addIceCandidate(candidate?: RTCIceCandidateInit): Promise<void> {
    await this.original.addIceCandidate?.(candidate);
  }
  async setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void> {
    await this.original.setRemoteDescription?.(description);
  }
  async setLocalDescription(description?: RTCSessionDescriptionInit): Promise<void> {
    await this.original.setLocalDescription?.(description);
  }
  get localDescription(): RTCSessionDescription | null {
    return this.original.localDescription ?? null;
  }
  get signalingState(): RTCSignalingState {
    return this.original.signalingState ?? 'closed';
  }
  get iceConnectionState(): RTCIceConnectionState {
    return this.original.iceConnectionState ?? 'closed';
  }
}
